/**
 * **Emisores fiscales (ARCA)** — alta de CUITs, pedido de certificado (CSR), carga del
 * `.crt`, prueba de conexión y punto de venta por sede. Contrato en
 * `docs/api/contracts.md` § «Emisores fiscales».
 */
import { z } from "zod";
import { objectId } from "./base.js";
export const FISCAL_EMITTER_CONDITIONS = [
    "responsable_inscripto",
    "monotributo",
    "exento",
];
export const ARCA_ENVIRONMENTS = ["homologacion", "produccion"];
/** CUIT: 11 dígitos; se aceptan guiones al tipear. El dígito verificador lo valida el backend. */
const cuitInput = z
    .string()
    .trim()
    .transform((v) => v.replace(/[-\s.]/g, ""))
    .pipe(z.string().regex(/^\d{11}$/, "El CUIT son 11 dígitos"));
const branchPointOfSale = z.object({
    branchId: objectId,
    pointOfSale: z.number().int().min(1).max(99_998),
});
/**
 * RN-CON-05: remitente de los comprobantes de un PV de este emisor. Acepta la dirección
 * sola o con nombre («Viva Training <ventas@club.com.ar>»).
 */
const emailProfile = z.object({
    pointOfSale: z.number().int().min(1).max(99_998),
    from: z
        .string()
        .trim()
        .max(200)
        .refine((v) => /^[^<>@\s]+@[^<>@\s]+\.[^<>@\s]+$/.test(v) ||
        /^[^<>]*<[^<>@\s]+@[^<>@\s]+\.[^<>@\s]+>$/.test(v), "El remitente es una dirección de mail («ventas@club.com.ar» o «Nombre <ventas@club.com.ar>»)"),
});
export const createFiscalEmitterBody = z.object({
    name: z.string().trim().min(2).max(80),
    cuit: cuitInput,
    businessName: z.string().trim().min(2).max(200),
    fiscalCondition: z.enum(FISCAL_EMITTER_CONDITIONS),
    environment: z.enum(ARCA_ENVIRONMENTS).default("homologacion"),
    isDefaultGym: z.boolean().optional(),
    isDefaultKiosk: z.boolean().optional(),
    address: z.string().trim().max(200).optional(),
    grossIncome: z.string().trim().max(60).optional(),
    activityStartDate: z.string().trim().max(20).optional(),
    notes: z.string().trim().max(1000).optional(),
});
/** El CUIT y el ambiente NO se editan: definen la numeración. Para producción, duplicar. */
export const updateFiscalEmitterBody = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    businessName: z.string().trim().min(2).max(200).optional(),
    fiscalCondition: z.enum(FISCAL_EMITTER_CONDITIONS).optional(),
    isDefaultGym: z.boolean().optional(),
    isDefaultKiosk: z.boolean().optional(),
    active: z.boolean().optional(),
    address: z.string().trim().max(200).optional(),
    grossIncome: z.string().trim().max(60).optional(),
    activityStartDate: z.string().trim().max(20).optional(),
    notes: z.string().trim().max(1000).optional(),
    branchPointsOfSale: z.array(branchPointOfSale).max(50).optional(),
    /** Reemplaza la lista entera: un PV que no viene queda sin perfil (sin mail). */
    emailProfiles: z.array(emailProfile).max(50).optional(),
});
/** Paso 1: generar clave + CSR. El alias es opcional (se sugiere uno). */
export const generateCsrBody = z.object({
    alias: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9]{3,40}$/, "El alias va sólo con letras y números (3 a 40)")
        .optional(),
});
/** Paso 2: el certificado que devolvió ARCA, pegado como texto o subido como archivo. */
export const uploadEmitterCertificateBody = z
    .object({
    certPem: z
        .string()
        .max(64 * 1024)
        .optional(),
    /** Archivo `.crt`/`.pem` en base64 (la pantalla lo lee con FileReader). */
    contentBase64: z
        .string()
        .max(400 * 1024)
        .optional(),
    filename: z.string().max(200).optional(),
})
    .refine((b) => Boolean(b.certPem?.trim() || b.contentBase64), {
    message: "Falta el certificado: pegalo o subí el archivo.",
});
/** Alternativa: «ya tengo el certificado y la clave» (PEM sueltos o un `.p12`). */
export const uploadEmitterCredentialsBody = z
    .object({
    certPem: z
        .string()
        .max(64 * 1024)
        .optional(),
    keyPem: z
        .string()
        .max(64 * 1024)
        .optional(),
    p12Base64: z
        .string()
        .max(400 * 1024)
        .optional(),
    passphrase: z.string().max(200).optional(),
})
    .refine((b) => Boolean(b.p12Base64) || Boolean(b.certPem?.trim() && b.keyPem?.trim()), {
    message: "Subí el certificado y la clave, o el .p12 con su contraseña.",
});
/** La sede elige con qué emisor factura (`null` = el del gimnasio por defecto). */
export const branchFiscalEmitterBody = z.object({
    fiscalEmitterId: objectId.nullable(),
});
//# sourceMappingURL=fiscal-emitters.js.map